<single-select>
    <div class="relative {isFitContent() ? 'ss-fit inline-flex max-w-full' : 'flex w-full'}">
        <!-- Trigger: looks like a native select -->
        <button type="button"
                id="{state.triggerId}"
                class="ss-trigger select select-bordered bg-white w-full text-start text-sm font-medium cursor-pointer min-w-0"
                disabled="{props.disabled}"
                aria-haspopup="listbox"
                aria-expanded="{state.open}"
                aria-controls="{state.listboxId}"
                aria-labelledby="{props.labelledby || null}"
                aria-label="{!props.labelledby && props.arialabel ? props.arialabel : null}"
                aria-disabled="{props.loading ? 'true' : null}"
                aria-busy="{props.loading ? 'true' : null}"
                onclick="{toggleDropdown}"
                onkeydown="{handleTriggerKeydown}">
            <span class="ss-value truncate {selectedLabel() ? (selectedColor() || 'text-primary') : 'text-gray-500'}">
                {selectedLabel() || props.placeholder || locale?.t('none')}
            </span>
            <!-- fitcontent only: hidden sizer listing every option so the button
                 sizes to the widest option (no measured px / min-width). -->
            <span class="ss-sizer" if="{isFitContent()}" aria-hidden="true">
                <span each="{o in state.options}">{o.label}</span>
                <span>{props.placeholder || noneLabel()}</span>
            </span>
        </button>

        <!-- Dropdown: rendered in the top layer via the Popover API so it escapes
             any ancestor overflow / transform containing block (e.g. daisyUI
             .modal-box), just like a native <select> popup. Positioned with
             fixed coords anchored to the trigger. -->
        <div id="{state.popupId}"
             popover="{state.popoverMode}"
             class="ss-popup bg-base-100 border border-base-300 rounded-box overflow-hidden shadow-md {state.open ? 'flex flex-col' : 'hidden'}">
            <div class="sticky top-0 bg-base-100 px-3 py-2 border-b border-base-300">
                <div class="relative">
                    <input type="text"
                           id="{state.searchId}"
                           size="1"
                           aria-label="{locale?.t('search')}"
                           placeholder="{locale?.t('search')}"
                           value="{state.searchText}"
                           oninput="{handleSearch}"
                           onclick="{(e) => e.stopPropagation()}"
                           onkeydown="{handleSearchKeydown}"
                           class="input input-sm input-bordered w-full min-w-32 pe-8">
                    <button type="button" if="{state.searchText}"
                            class="absolute inset-y-0 end-2 z-10 flex items-center text-gray-500 hover:text-primary cursor-pointer"
                            aria-label="{locale?.t('clearsearch')}"
                            tabindex="-1"
                            onclick="{clearSearch}">
                        <i class="icon-clear" aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            <ul role="listbox"
                aria-labelledby="{props.labelledby || null}"
                id="{state.listboxId}"
                class="overflow-y-auto flex-1 flex flex-col w-full min-h-0 outline-none"
                tabindex="-1">

                <!-- None / clear row (shown by default; suppress with hidenone; hidden while filtering) -->
                <li if="{showNone()}"
                    role="option"
                    aria-selected="{!props.selected}"
                    tabindex="{state.activeIndex === -1 ? 0 : -1}"
                    id="{state.listboxId}-option-none"
                    onclick="{selectNone}"
                    onkeydown="{onNoneKeydown}"
                    class="w-full hover:bg-base-200 focus:bg-base-200 focus:outline-none cursor-pointer {!props.selected ? 'bg-base-200' : ''}">
                    <span class="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-primary">
                        <span class="w-4 shrink-0 flex items-center justify-center" aria-hidden="true">{!props.selected ? '✓' : ''}</span>
                        <span class="flex-1 break-words">{noneLabel()}</span>
                    </span>
                </li>

                <li each="{(item, idx) in state.filteredOptions}"
                    role="option"
                    aria-selected="{item.value === props.selected}"
                    tabindex="{state.activeIndex === idx ? 0 : -1}"
                    id="{state.listboxId}-option-{idx}"
                    onclick="{() => selectOption(item)}"
                    onkeydown="{(e) => onOptionKeydown(e, item, idx)}"
                    class="w-full hover:bg-base-200 focus:bg-base-200 focus:outline-none cursor-pointer {item.value === props.selected ? 'bg-base-200' : ''}">
                    <span class="flex items-center gap-2 w-full px-3 py-2 text-sm {item.color || 'text-primary'} {item.value === props.selected ? 'font-semibold' : 'font-medium'}">
                        <span class="w-4 shrink-0 flex items-center justify-center" aria-hidden="true">{item.value === props.selected ? '✓' : ''}</span>
                        <span class="flex-1 break-words">{item.label}</span>
                    </span>
                </li>

                <li if="{state.filteredOptions.length === 0}"
                    class="px-3 py-2 text-sm text-gray-500 italic"
                    aria-disabled="true">
                    {locale?.t('no_results')}
                </li>
            </ul>
        </div>
    </div>

    <script>
        // Module-scope counter -> unique aria ids per instance (mirrors multi-select.tag)
        let singleSelectInstanceCounter = 0;

        export default {
            includeTools: false,

            state: {
                open: false,
                options: [],          // [{ label, value, item }]
                filteredOptions: [],
                searchText: '',
                activeIndex: -1,      // -1 = None row / nothing; else idx into filteredOptions
                triggerId: '',
                searchId: '',
                listboxId: '',
                popupId: '',
                popoverMode: 'manual', // null when the Popover API is unsupported (fallback)
                lastListSnapshot: ''
            },

            props: {
                list: null,             // any array
                idproperty: null,       // value key, default 'id'
                displayproperty: null,  // label key, default 'displayName'
                colorproperty: null,    // optional key holding a text-color class per item (e.g. status/action colors)
                fitcontent: true,       // DEFAULT: size the trigger to the widest option; pass fitcontent="{false}" to fill the parent instead
                selected: null,         // current value (parent-controlled)
                placeholder: null,
                loading: false,         // when true, the control renders blank + non-interactive until data arrives
                hidenone: false,        // by default a clearable "None" row is shown; set to hide it
                nonelabel: null,        // optional label for the None row (defaults to the 'none' locale phrase)
                arialabel: null,        // accessible name when there is no external <label> (labelledby)
                labelledby: null,       // id of an external <label>
                disabled: false,
                onupdated: null         // (value, item) => void
            },

            onBeforeMount() {
                const n = ++singleSelectInstanceCounter;
                this.state.triggerId = 'single-select-' + n + '-trigger';
                this.state.searchId  = 'single-select-' + n + '-search';
                this.state.listboxId = 'single-select-' + n + '-listbox';
                this.state.popupId   = 'single-select-' + n + '-popup';
                this.state.popoverMode = (typeof HTMLElement !== 'undefined'
                    && typeof HTMLElement.prototype.showPopover === 'function') ? 'manual' : null;
                this._reposition = () => this.positionPopup();
                this.buildOptions();
            },
            onMounted() { document.addEventListener('click', this.onClickOutside); },
            onBeforeUpdate() { this.buildOptions(); },
            onBeforeUnmount() {
                document.removeEventListener('click', this.onClickOutside);
                this.removeRepositionListeners();
            },

            buildOptions() {
                const idProp = this.props.idproperty || 'id';
                const displayProp = this.props.displayproperty || 'displayName';
                const colorProp = this.props.colorproperty;
                const list = this.props.list || [];
                const snapshot = JSON.stringify(list);
                if (snapshot !== this.state.lastListSnapshot) {
                    this.state.lastListSnapshot = snapshot;
                    this.state.options = list.map(i => ({ label: i[displayProp], value: i[idProp], item: i, color: colorProp ? (i[colorProp] || '') : '' }));
                    if (!this.state.open) this.state.filteredOptions = this.state.options;
                }
            },

            selectedLabel() {
                const m = this.state.options.find(o => o.value === this.props.selected);
                return m ? m.label : '';
            },

            selectedColor() {
                const m = this.state.options.find(o => o.value === this.props.selected);
                return m ? (m.color || '') : '';
            },

            // Content-sizing is the default; only an explicit false (boolean or
            // string) opts out (an unset prop is undefined → still on).
            isFitContent() { return this.props.fitcontent !== false && this.props.fitcontent !== 'false'; },

            noneLabel() { return this.props.nonelabel || this.locale?.t('none'); },
            showNone() { return !this.props.hidenone && !this.state.searchText; },

            popupEl() { return document.getElementById(this.state.popupId); },

            positionPopup() {
                const trigger = document.getElementById(this.state.triggerId);
                const popup = this.popupEl();
                if (!trigger || !popup) return;
                const rect = trigger.getBoundingClientRect();
                const gap = 4;
                const margin = 8; // keep the menu off the viewport edge
                const isRtl = getComputedStyle(trigger).direction === 'rtl';
                const spaceBelow = window.innerHeight - rect.bottom;
                const spaceAbove = rect.top;
                const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
                const maxH = Math.max(160, Math.min(384, (openUp ? spaceAbove : spaceBelow) - gap - 8));
                popup.style.position = 'fixed';
                popup.style.margin = '0';
                popup.style.padding = '0';
                popup.style.inset = 'auto';
                popup.style.maxHeight = maxH + 'px';

                // Menu width = clamp(widest option, trigger width, cap):
                //  - width:fit-content grows the menu to the widest option so
                //    normal labels never wrap;
                //  - min-width = trigger width so the menu is never narrower than
                //    the button;
                //  - max-width is capped (not the full viewport) so a sentence-
                //    length option wraps (labels have break-words) instead of
                //    ballooning.
                // Anchored to the trigger's leading edge — left in LTR, right in
                // RTL — so it spills toward the trailing side.
                const MENU_MAX_WIDTH = 448; // px — "not enormous"; longer options wrap
                popup.style.width = 'fit-content';
                popup.style.minWidth = Math.round(rect.width) + 'px';
                if (isRtl) {
                    popup.style.left = 'auto';
                    popup.style.right = Math.round(window.innerWidth - rect.right) + 'px';
                    popup.style.maxWidth = Math.min(MENU_MAX_WIDTH, Math.round(rect.right - margin)) + 'px';
                } else {
                    popup.style.right = 'auto';
                    popup.style.left = Math.round(rect.left) + 'px';
                    popup.style.maxWidth = Math.min(MENU_MAX_WIDTH, Math.round(window.innerWidth - rect.left - margin)) + 'px';
                }

                if (openUp) {
                    popup.style.top = 'auto';
                    popup.style.bottom = Math.round(window.innerHeight - rect.top + gap) + 'px';
                } else {
                    popup.style.bottom = 'auto';
                    popup.style.top = Math.round(rect.bottom + gap) + 'px';
                }
            },

            addRepositionListeners() {
                window.addEventListener('scroll', this._reposition, true);
                window.addEventListener('resize', this._reposition);
            },
            removeRepositionListeners() {
                window.removeEventListener('scroll', this._reposition, true);
                window.removeEventListener('resize', this._reposition);
            },

            toggleDropdown() {
                if (this.props.disabled || this.props.loading) return;
                this.state.open ? this.closeDropdown(false) : this.openDropdown('search');
            },
            openDropdown(initialFocus) {
                this.update({ open: true, searchText: '', filteredOptions: this.state.options, activeIndex: -1 });
                const popup = this.popupEl();
                if (popup) {
                    this.positionPopup();
                    if (this.state.popoverMode && popup.showPopover && !popup.matches(':popover-open')) {
                        try { popup.showPopover(); } catch (e) {}
                    }
                    this.addRepositionListeners();
                }
                if (initialFocus === 'first' && this.state.filteredOptions.length) this.focusOption(0);
                else if (initialFocus === 'last' && this.state.filteredOptions.length) this.focusOption(this.state.filteredOptions.length - 1);
                else this.focusSearch();
            },
            closeDropdown(restoreFocus) {
                const popup = this.popupEl();
                if (popup && this.state.popoverMode && popup.hidePopover && popup.matches(':popover-open')) {
                    try { popup.hidePopover(); } catch (e) {}
                }
                this.removeRepositionListeners();
                this.update({ open: false, searchText: '', filteredOptions: this.state.options, activeIndex: -1 });
                if (restoreFocus) setTimeout(() => document.getElementById(this.state.triggerId)?.focus(), 0);
            },
            focusSearch() { setTimeout(() => document.getElementById(this.state.searchId)?.focus(), 0); },
            focusOption(idx) {
                this.update({ activeIndex: idx });
                setTimeout(() => document.getElementById(this.state.listboxId + '-option-' + idx)?.focus(), 0);
            },
            focusNone() {
                this.update({ activeIndex: -1 });
                setTimeout(() => document.getElementById(this.state.listboxId + '-option-none')?.focus(), 0);
            },

            handleSearch(e) {
                const searchText = e.target.value;
                const filteredOptions = this.state.options.filter(o => (o.label || '').toLowerCase().includes(searchText.toLowerCase()));
                this.update({ searchText, filteredOptions, activeIndex: -1 });
                this.positionPopup();
            },
            clearSearch() {
                this.update({ searchText: '', filteredOptions: this.state.options, activeIndex: -1 });
                this.positionPopup();
                this.focusSearch();
            },

            handleTriggerKeydown(e) {
                if (this.props.disabled || this.props.loading) return;
                if (e.key === 'ArrowDown') { e.preventDefault(); this.state.open ? this.focusFirstRow() : this.openDropdown('first'); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); this.state.open ? this.focusLastRow() : this.openDropdown('last'); }
                else if ((e.key === 'Enter' || e.key === ' ') && !this.state.open) { e.preventDefault(); this.openDropdown('search'); }
                else if (e.key === 'Escape' && this.state.open) { e.preventDefault(); this.closeDropdown(true); }
            },
            handleSearchKeydown(e) {
                if (e.key === 'ArrowDown') { e.preventDefault(); this.focusFirstRow(); }
                else if (e.key === 'Enter') { e.preventDefault(); if (this.state.filteredOptions.length) this.selectOption(this.state.filteredOptions[0]); }
                else if (e.key === 'Escape') { e.preventDefault(); this.closeDropdown(true); }
                else if (e.key === 'Tab') { this.closeDropdown(false); }
            },
            onOptionKeydown(e, item, idx) {
                if (e.key === 'ArrowDown') { e.preventDefault(); if (idx < this.state.filteredOptions.length - 1) this.focusOption(idx + 1); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); idx === 0 ? (this.showNone() ? this.focusNone() : this.focusSearch()) : this.focusOption(idx - 1); }
                else if (e.key === 'Home') { e.preventDefault(); this.focusOption(0); }
                else if (e.key === 'End') { e.preventDefault(); this.focusOption(this.state.filteredOptions.length - 1); }
                else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); this.selectOption(item); }
                else if (e.key === 'Escape') { e.preventDefault(); this.closeDropdown(true); }
                else if (e.key === 'Tab') { this.closeDropdown(false); }
            },
            onNoneKeydown(e) {
                if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); this.selectNone(); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); if (this.state.filteredOptions.length) this.focusOption(0); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); this.focusSearch(); }
                else if (e.key === 'Escape') { e.preventDefault(); this.closeDropdown(true); }
                else if (e.key === 'Tab') { this.closeDropdown(false); }
            },
            focusFirstRow() { this.showNone() ? this.focusNone() : (this.state.filteredOptions.length && this.focusOption(0)); },
            focusLastRow() { this.state.filteredOptions.length ? this.focusOption(this.state.filteredOptions.length - 1) : (this.showNone() && this.focusNone()); },

            selectOption(item) { this.emit(item.value, item.item); this.closeDropdown(true); },
            selectNone() { this.emit('', null); this.closeDropdown(true); },
            emit(value, item) { if (typeof this.props.onupdated === 'function') this.props.onupdated(value, item); },

            onClickOutside(e) { if (!this.root.contains(e.target) && this.state.open) this.closeDropdown(false); }
        }
    </script>

    <style>
        /* fitcontent: overlay the current label and a hidden sizer (all option
           labels) in one grid cell so the trigger sizes to the widest option —
           no measured width, no min-width; adapts to translations + font. */
        single-select .ss-fit .ss-trigger { display: inline-grid; width: auto; }
        single-select .ss-fit .ss-value,
        single-select .ss-fit .ss-sizer { grid-area: 1 / 1; }
        single-select .ss-sizer { visibility: hidden; height: 0; overflow: hidden; pointer-events: none; }
        single-select .ss-sizer > span { display: block; white-space: nowrap; }
    </style>
</single-select>
